import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import styles from './ClubSwitcher.module.css';
import routes from 'routes';

import { ReactComponent as SwapIcon } from 'assets/images/icon_swap.svg';

import { getClubs } from 'api/clubApi';
import { updateClubs } from 'services/user/userSlice';
import { applySelectedClub, filterActiveClubs } from 'hooks/useEnsureClubs';

const PANEL_WIDTH = 360;

// Top-nav "Clubs" button (sits beside the active club name). Shown only to a user in 2 or more
// clubs (Doug, 2026-10-26): with one club there is nothing to switch to. It reads the clubs list
// already in memory, which is loaded when the site opens, so a member added to a second club sees
// the button at their next sign-in or page reload. Opening it re-fetches the clubs list (a cheap,
// server-cached call) so it's current when they look.
//
// Presented as a dropdown anchored under the trigger (not a centred modal): it reads as
// a navigation menu, so it closes on outside-click / Esc as well as the × or picking a club.
const ClubSwitcher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clubs, selectedClub } = useSelector((state) => state.user || {});

  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, left } — anchored just under the trigger
  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  // Pull a fresh clubs list whenever the menu opens — the redux copy is loaded
  // once at app start and can miss a club the user was added to mid-session.
  const refresh = useCallback(async () => {
    try {
      const response = await getClubs();
      if (response?.data) {
        dispatch(updateClubs(filterActiveClubs(response.data)));
      }
    } catch (err) {
      // Keep showing the list we already have; the menu still works offline-of-this.
      console.log('[ClubSwitcher] Failed to refresh clubs: ', err);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isOpen) {
      refresh();
    }
  }, [isOpen, refresh]);

  const close = () => setIsOpen(false);

  // Anchor the panel just under the trigger, clamped so it never spills past the viewport edge.
  const reposition = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_WIDTH - 8));
    setPos({ top: r.bottom + 6, left });
  }, []);

  const toggle = () => {
    if (!isOpen) reposition();
    setIsOpen((v) => !v);
  };

  // Dropdown behaviour while open: outside-click / Esc close it, and it tracks the trigger on
  // scroll/resize. The trigger is excluded from the outside-click check so its own onClick toggles cleanly.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (e) => {
      if (panelRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      close();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true); // capture: follow scrolls in any container
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [isOpen, reposition]);

  const choose = async (club) => {
    if (club.clubID !== selectedClub?.clubID) {
      await applySelectedClub(club, dispatch);
    }
    close();
    navigate(routes.admin.dashboard);
  };

  const clubList = clubs || [];

  if (clubList.length < 2) return null;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={styles.trigger}
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Switch club"
      >
        <SwapIcon className={styles.triggerIcon} />
        <span>Clubs</span>
      </button>

      {isOpen &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className={styles.panel}
            style={{ top: pos.top, left: pos.left, width: PANEL_WIDTH }}
            role="menu"
          >
            <div className={styles.header}>
              <h2 className={styles.title}>Your clubs</h2>
              <button type="button" className={styles.closeBtn} onClick={close} aria-label="Close">
                &times;
              </button>
            </div>

            <div className={styles.body}>
              <p className={styles.hint}>Select a club to switch to it.</p>
              <ul className={styles.list}>
                {clubList.map((club) => {
                  const isCurrent = club.clubID === selectedClub?.clubID;
                  return (
                    <li key={club.clubID}>
                      <button
                        type="button"
                        className={`${styles.item} ${isCurrent ? styles.itemCurrent : ''}`}
                        onClick={() => choose(club)}
                      >
                        <span className={styles.itemName}>{club.clubName}</span>
                        <span className={styles.itemMeta}>
                          <span className={styles.role}>{club.userRole}</span>
                          {isCurrent && <span className={styles.currentTag}>Current</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ClubSwitcher;
